/**
 * Central fetch wrapper.
 *
 * Responsibilities (in this order):
 *   1. Send credentials (cookies — pa_session / pa_refresh / pa_csrf).
 *   2. Attach the X-CSRF-Token header from the pa_csrf cookie on writes.
 *   3. Normalize errors into ApiError so consumers can write `catch (e: ApiError)`.
 *   4. On 401 from a non-auth route, dispatch a global "auth:expired" event so
 *      the route guard can redirect to /login. Don't redirect from inside the
 *      fetch — it must work outside React too.
 *   5. Surface CSV / blob responses transparently (return Blob instead of JSON).
 *
 * No business logic here. TanStack Query wraps it for caching/loading state.
 */

const BASE_URL =
  ((import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE_URL ?? '/api/v1') as string;

const CSRF_COOKIE = 'pa_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const key = part.slice(0, eq).trim();
    if (key === name) return decodeURIComponent(part.slice(eq + 1));
  }
  return null;
}

export class ApiError extends Error {
  code: string;
  status: number;
  details: Record<string, unknown>;
  requestId: string | null;
  constructor(opts: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(opts.message);
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details ?? {};
    this.requestId = opts.requestId ?? null;
  }
}

const AUTH_EXPIRED_EVENT = 'auth:expired';
function emitAuthExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT' | 'HEAD' | 'OPTIONS';

export interface ApiOpts {
  method?: ApiMethod;
  body?: unknown;
  query?: object;
  /** When true, return the raw Response (use for CSV / streaming). */
  raw?: boolean;
  /** Skip the global 401 redirect — used by `/auth/me` so a logged-out user
   *  can land on /login without an event loop. */
  silentUnauth?: boolean;
  /** When set, used to abort the in-flight request (TanStack Query passes this in). */
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: object): string {
  const base = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;
  const u = new URL(base, window.location.origin);
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === null || item === undefined || item === '') continue;
        u.searchParams.append(k, String(item));
      }
      continue;
    }
    u.searchParams.set(k, String(v));
  }
  return u.toString().replace(window.location.origin, '');
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const method: ApiMethod = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // Attach CSRF on writes — the cookie is JS-readable by design.
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  const url = buildUrl(path, opts.query);
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (opts.raw) {
    if (!res.ok) {
      throw await toApiError(res);
    }
    return res as unknown as T;
  }

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  if (!res.ok) {
    const err = await toApiError(res);
    if (err.status === 401 && !opts.silentUnauth) {
      emitAuthExpired();
    }
    throw err;
  }

  const text = await res.text();
  if (!text) return undefined as unknown as T;
  return JSON.parse(text) as T;
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = res.statusText || `HTTP ${res.status}`;
  let details: Record<string, unknown> = {};
  let requestId: string | null = null;
  try {
    const body = await res.json();
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details ?? {};
      requestId = body.error.request_id ?? null;
    }
  } catch {
    /* ignore */
  }
  return new ApiError({ code, message, status: res.status, details, requestId });
}

/**
 * Initialize CSRF: call once on app boot so the cookie is set before the
 * user does anything that requires it.  Idempotent.
 */
export async function ensureCsrf(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) return;
  await api<{ csrf_token: string }>('/auth/csrf');
}

/**
 * Download a CSV response as an `<a download>` blob.
 */
/** The marker the API writes into a CSV it had to cut short. Must match
 *  `_TRUNCATION_MARKER` in polymarket-api/app/csv_stream.py. */
export const CSV_TRUNCATION_MARKER = '*** TRUNCATED ***';

/**
 * Download a server-streamed CSV, and say whether it is COMPLETE.
 *
 * Server exports are capped (250,000 rows) because they are otherwise
 * unbounded — the busiest event holds 4.28M trades. A capped file ends with a
 * marker row, and this reports whether that happened so the caller can tell the
 * user.
 *
 * Detected from the file rather than predicted from a row estimate: streaming
 * means the response is already a 200 with headers by the time the server
 * discovers there is more data, so truncation cannot be signalled out of band.
 * Reading the delivered bytes is the only way to know for certain, and
 * certainty is the point — a warning that fires on a complete export teaches
 * people to ignore it.
 */
export async function downloadCsv(
  path: string,
  query: object | undefined,
  filename: string,
): Promise<{ truncated: boolean }> {
  const merged = { ...((query as Record<string, unknown>) ?? {}), format: 'csv' };
  const res = await api<Response>(path, { query: merged, raw: true });
  const blob = await res.blob();
  // Only the tail can carry the marker, so do not read megabytes to find it.
  const tail = await blob.slice(Math.max(0, blob.size - 2048)).text();
  const truncated = tail.includes(CSV_TRUNCATION_MARKER);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { truncated };
}

export { AUTH_EXPIRED_EVENT };
